import React, { useState, useEffect, useRef } from "react";

import { 
  Campaign, 
  QuestBoard,
} from "@dndModels";

import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import ArchiveButton from "@components/input/buttons/icon_buttons/ArchiveButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FlexBox from "@components/layout/FlexBox";
import DisplayQuestBoard from "./DisplayQuestBoard";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

interface Props {
  campaign: Campaign;
  quest_board: QuestBoard;
  onDelete: () => void;
  onArchive: () => void;
  autoOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function QuestBoardInput({ campaign, quest_board, onDelete, onArchive, autoOpen, onOpenChange }: Props) {
  const [reloading, setReloading] = useState(false);
  const [edit_mode, setEditMode] = useState(false);
  // const [ping, setPing] = useState(false);
  const [display_mode, setDisplayMode] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // Auto-open on mount if the URL points at this letter — mirrors the same
  // owner-vs-viewer mode choice as the click handler below.
  useEffect(() => {
    if (autoOpen) {
      setDisplayMode(true);
      onOpenChange?.(true);
    }
    // Mount-only: don't want this refiring if autoOpen flips true later
    // while the user is already interacting with the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const openEditMode = () => {
    setEditMode(true);
    onOpenChange?.(true);
  };

  const closeEditMode = () => {
    setEditMode(false);
    onOpenChange?.(false);
  };

  const openDisplayMode = () => {
    setDisplayMode(true);
    onOpenChange?.(true);
  };

  const closeDisplayMode = () => {
    setDisplayMode(false);
    onOpenChange?.(false);
  };
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={edit_mode}
          full_page
          onClose={closeEditMode}>
          <DisplayQuestBoard
            campaign={campaign}
            quest_board={quest_board}
            edit
          />
        </ModalOrDrawer>
        <ModalOrDrawer
          open={display_mode}
          full_page
          onClose={closeDisplayMode}>
          <DisplayQuestBoard
            campaign={campaign}
            quest_board={quest_board}
          />
        </ModalOrDrawer>
      </>
    );
  }

  return (
    <div className="padded">
      <FlexBox direction="column" className={cx(classes.root)}>
        <FlexBox direction="row">
          <FlexBox className="header padded clickable" onClick={() => {
            if (api.user_id === campaign.owner_id) {
              openEditMode();
            } else {
              openDisplayMode();
            } 
          }}>{ quest_board.name }</FlexBox>
          { campaign.owner_id === api.user_id &&
            <>
              <FlexBox xs_expand={0}>
                <DandDButtonBox name="Display" onClick={openDisplayMode} />
              </FlexBox>
              <FlexBox xs_expand={0} className="centered">
                <div style={{ float: "right"}}>
                  <ArchiveButton
                    no_fab
                    is_archived={quest_board.is_archived}
                    onClick={onArchive}
                  />
                  <DeleteButton
                    no_fab
                    obj_name={quest_board.name}
                    onClick={onDelete}
                  />
                </div>
              </FlexBox>
            </>
          }
        </FlexBox>
      </FlexBox>
      { renderExtras() }
    </div>
  );
}
